if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.variantData = this.getVariantData();
        this.querySelector('.product-media-modal__content--gallery')?.addEventListener('pointerup', (event) => {
          event.stopPropagation();
        });

        this.querySelectorAll('.product-media-modal__thumb').forEach((button) => {
          button.addEventListener('click', () => {
            this.setActiveMedia(button.dataset.mediaId);
          });
        });
      }

      hide() {
        super.hide();
        if (document.querySelector('quick-add-modal[open]')) {
          document.body.classList.add('overflow-hidden');
        }
      }

      show(opener) {
        super.show(opener);
        this.syncCurrentVariant();
        this.showActiveMedia();
      }

      showActiveMedia() {
        const openedMediaId = this.openedBy?.getAttribute('data-media-id');
        const variantId = this.getCurrentVariantId();
        const variant = this.getVariantById(variantId);
        const allMediaIds = this.showAllMedia();
        const activeMediaId =
          openedMediaId || this.dataset.currentMediaId || variant?.featuredMediaId || allMediaIds[0];

        this.setActiveMedia(activeMediaId);
      }

      setActiveMedia(mediaId) {
        const allMediaItems = Array.from(this.querySelectorAll('.product-media-modal__stage [data-media-id]'));
        const activeMedia = allMediaItems.find((element) => element.dataset.mediaId === String(mediaId)) || allMediaItems[0];
        if (!activeMedia) return;

        allMediaItems.forEach((element) => {
          element.classList.remove('active');
        });

        const thumbnails = Array.from(this.querySelectorAll('.product-media-modal__thumb'));
        thumbnails.forEach((button) => {
          const isActive = button.dataset.mediaId === activeMedia.dataset.mediaId;
          button.classList.toggle('active', isActive);
          button.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        const activeThumbnail = thumbnails.find((button) => button.dataset.mediaId === activeMedia.dataset.mediaId);
        this.updateProductDetails(this.getVariantById(this.getCurrentVariantId()), activeThumbnail);

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        activeThumbnail?.scrollIntoView({ block: 'nearest' });

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      setCurrentVariant(variantId, mediaId) {
        if (variantId) this.dataset.currentVariantId = String(variantId);
        if (mediaId) this.dataset.currentMediaId = String(mediaId);
        this.showAllMedia();
      }

      syncCurrentVariant() {
        const variantId = this.getCurrentVariantId();
        if (!variantId) return;

        const variant = this.getVariantById(variantId);
        if (variant?.featuredMediaId) this.dataset.currentMediaId = String(variant.featuredMediaId);
        this.dataset.currentVariantId = String(variantId);
      }

      filterMediaByVariant(variantId) {
        const visibleMediaIds = this.showAllMedia();
        const variant = this.getVariantById(variantId);
        this.syncThumbnailInfo(variant);
        return visibleMediaIds;
      }

      showAllMedia() {
        const mediaItems = Array.from(this.querySelectorAll('.product-media-modal__stage [data-media-id]'));
        const thumbnails = Array.from(this.querySelectorAll('.product-media-modal__thumb'));

        mediaItems.forEach((element) => element.classList.remove('is-hidden-by-variant'));
        thumbnails.forEach((button) => button.classList.remove('is-hidden-by-variant'));

        return mediaItems.map((element) => String(element.dataset.mediaId));
      }

      getGroupedMediaIdsForVariant(mediaItems, variantId) {
        if (!variantId) return [];

        const targetVariantId = String(variantId);
        const visibleMediaIds = [];
        let currentVariantIds = [];

        mediaItems.forEach((element) => {
          const directVariantIds = this.parseIdList(element.dataset.variantIds);
          if (directVariantIds.length) currentVariantIds = directVariantIds;

          const mediaVariantIds = directVariantIds.length ? directVariantIds : currentVariantIds;
          if (mediaVariantIds.includes(targetVariantId)) {
            visibleMediaIds.push(String(element.dataset.mediaId));
          }
        });

        return [...new Set(visibleMediaIds)];
      }

      updateProductDetails(variant, activeThumbnail) {
        if (!variant && !activeThumbnail) return;

        const thumbnailVariantIds = this.parseIdList(activeThumbnail?.dataset.variantIds);
        const useVariantDetails =
          variant && (!thumbnailVariantIds.length || thumbnailVariantIds.includes(String(variant.id)));
        const variantSpec = variant?.title === 'Default Title' ? 'Default' : variant?.title;
        const spec = useVariantDetails ? variantSpec : activeThumbnail?.dataset.spec || variantSpec;
        const fields = [
          ['[data-modal-current-title]', this.dataset.productTitle || activeThumbnail?.dataset.productTitle],
          ['[data-modal-current-spec]', spec],
          ['[data-modal-current-sku]', useVariantDetails ? variant?.sku : activeThumbnail?.dataset.sku || variant?.sku],
          [
            '[data-modal-current-part-number]',
            useVariantDetails ? variant?.partNumber : activeThumbnail?.dataset.partNumber || variant?.partNumber,
          ],
        ];

        fields.forEach(([selector, value]) => {
          const element = this.querySelector(selector);
          if (element) element.textContent = value || 'N/A';
        });
      }

      syncThumbnailInfo(variant) {
        if (!variant) return;

        const spec = variant.title === 'Default Title' ? 'Default' : variant.title;
        this.querySelectorAll('.product-media-modal__thumb:not(.is-hidden-by-variant)').forEach((button) => {
          const fields = [
            ['[data-modal-thumb-title]', this.dataset.productTitle],
            ['[data-modal-thumb-spec]', spec],
            ['[data-modal-thumb-sku]', variant.sku],
            ['[data-modal-thumb-part-number]', variant.partNumber],
          ];

          fields.forEach(([selector, value]) => {
            const element = button.querySelector(selector);
            if (element) element.textContent = value || 'N/A';
          });
        });
      }

      getVariantData() {
        try {
          return JSON.parse(this.querySelector('[data-product-modal-variants]')?.textContent || '[]');
        } catch {
          return [];
        }
      }

      getVariantById(variantId) {
        return this.variantData.find((variant) => String(variant.id) === String(variantId));
      }

      getCurrentVariantId() {
        const sectionId = this.id.replace('ProductModal-', '');
        const formIds = [
          `product-form-${sectionId}`,
          `product-form-${sectionId}-desktop`,
          `product-form-installment-${sectionId}`,
        ];

        for (const formId of formIds) {
          const input = document.getElementById(formId)?.querySelector('input[name="id"]');
          if (input?.value) return input.value;
        }

        return this.dataset.currentVariantId || new URLSearchParams(window.location.search).get('variant');
      }

      parseIdList(value) {
        return (value || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
      }
    }
  );
}
